import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  Brain,
  Database,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  BarChart3,
  Target,
  Users,
  Sparkles,
  Lock,
  Award,
  TrendingUp,
  Search,
  Filter,
  Layers,
  GitBranch,
  Cpu,
  Globe,
  BookOpen,
  MessageSquare,
  Video,
  Lightbulb,
  Rocket,
  Info,
  Play,
  PauseCircle,
  ChevronRight,
  AlertTriangle,
  CheckSquare,
  Settings,
  RefreshCw,
  Eye
} from 'lucide-react'

interface ProcessStep {
  number: string
  title: string
  description: string
  duration: string
  icon: React.ReactNode
  details: string[]
  deliverables?: string[]
}

interface MethodologyPillar {
  title: string
  icon: React.ReactNode
  description: string
  features: string[]
  color: string
}

export const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [activeStep, setActiveStep] = useState(0)
  const [expandedMethod, setExpandedMethod] = useState<number | null>(null)
  const [showVideo, setShowVideo] = useState(false)
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const processSteps: ProcessStep[] = [
    {
      number: '01',
      title: 'Define Your Strategic Question',
      description: 'You submit your strategic challenge or decision through our secure platform',
      duration: '5 minutes',
      icon: <Target className="h-6 w-6" />,
      details: [
        'Submit your strategic question via secure form',
        'Choose analysis type (market entry, competitive, M&A, etc.)',
        'Provide business context and specific objectives',
        'Upload any relevant documents or data',
        'Set priority level and deadline'
      ],
      deliverables: [
        'Structured brief confirmation',
        'Analysis timeline',
        'Success metrics defined'
      ]
    },
    {
      number: '02',
      title: 'AI-Powered Intelligence Gathering',
      description: 'Our AI engine analyzes millions of data points across multiple sources',
      duration: '15 minutes',
      icon: <Database className="h-6 w-6" />,
      details: [
        'Scans 50+ premium data sources',
        'Analyzes market reports and industry databases',
        'Reviews competitor data and financial filings',
        'Processes news, patents, and regulatory information',
        'Identifies relevant trends and patterns',
        'Cross-references and validates data points'
      ],
      deliverables: [
        'Raw data compilation',
        'Initial pattern identification',
        'Data quality assessment'
      ]
    },
    {
      number: '03',
      title: 'Advanced Analysis & Modeling',
      description: 'Proprietary algorithms transform data into strategic insights',
      duration: '20 minutes',
      icon: <Brain className="h-6 w-6" />,
      details: [
        'Applies industry-specific analytical frameworks',
        'Runs financial models and scenario analysis',
        'Performs competitive positioning assessment',
        'Calculates market opportunity sizing',
        'Identifies risks and mitigation strategies',
        'Generates predictive insights and forecasts'
      ],
      deliverables: [
        'Strategic frameworks applied',
        'Quantitative models',
        'Risk-opportunity matrix'
      ]
    },
    {
      number: '04',
      title: 'Expert Validation & Quality Assurance',
      description: 'Human experts review and enhance AI-generated insights',
      duration: '10 minutes',
      icon: <Shield className="h-6 w-6" />,
      details: [
        'Automated quality validation system',
        'Validates assumptions and methodologies',
        'Adds industry-specific nuances',
        'Ensures logical consistency',
        'Checks for potential biases',
        'Enhances recommendations with expertise'
      ],
      deliverables: [
        'Quality assurance report',
        'Expert annotations',
        'Confidence scores'
      ]
    },
    {
      number: '05',
      title: 'Delivery & Strategic Recommendations',
      description: 'Receive comprehensive analysis with actionable recommendations',
      duration: '4 hours delivery',
      icon: <FileText className="h-6 w-6" />,
      details: [
        '45 minutes: AI analysis & processing',
        '2 hours: Quality validation & expert review',
        '1 hour: Report formatting & final delivery',
        '15 minutes: Buffer for peak times',
        'Executive summary with key findings',
        'Detailed analysis report (30-50 pages)',
        'Visual reports and infographics'
      ],
      deliverables: [
        'Executive presentation',
        'Detailed report',
        'Implementation guide',
        'Report access'
      ]
    }
  ]

  const methodologyPillars: MethodologyPillar[] = [
    {
      title: 'Multi-Source Intelligence',
      icon: <Globe className="h-6 w-6" />,
      description: 'We aggregate and analyze data from 50+ premium sources',
      features: [
        'Industry reports & market research',
        'Financial databases & SEC filings',
        'Patent & regulatory databases',
        'News & social sentiment analysis',
        'Competitive intelligence platforms',
        'Economic & demographic data'
      ],
      color: 'from-blue-600 to-cyan-600'
    },
    {
      title: 'Proprietary AI Engine',
      icon: <Cpu className="h-6 w-6" />,
      description: 'Advanced algorithms trained on thousands of business cases',
      features: [
        'Natural language processing',
        'Pattern recognition & anomaly detection',
        'Predictive modeling & forecasting',
        'Scenario simulation',
        'Industry-specific models',
        'Continuous learning & improvement'
      ],
      color: 'from-purple-600 to-pink-600'
    },
    {
      title: 'Strategic Frameworks',
      icon: <GitBranch className="h-6 w-6" />,
      description: 'Battle-tested methodologies from top consulting firms',
      features: [
        'Porter\'s Five Forces analysis',
        'SWOT & PESTLE assessment',
        'BCG Growth-Share Matrix',
        'McKinsey 7S Framework',
        'Blue Ocean Strategy',
        'Value Chain Analysis'
      ],
      color: 'from-green-600 to-emerald-600'
    },
    {
      title: 'Human Expertise',
      icon: <Users className="h-6 w-6" />,
      description: 'AI-powered analysis with expert-level insights',
      features: [
        'Industry specialization',
        'Quality assurance review',
        'Nuanced interpretation',
        'Custom recommendations',
        'Actionable insights',
        'Implementation guidance'
      ],
      color: 'from-orange-600 to-red-600'
    }
  ]

  const qualityAssurances = [
    {
      title: 'Data Accuracy',
      description: 'Every data point is verified across multiple sources',
      icon: <CheckSquare className="h-5 w-5" />
    },
    {
      title: 'Bias Detection',
      description: 'AI models trained to identify and mitigate analytical bias',
      icon: <Eye className="h-5 w-5" />
    },
    {
      title: 'Continuous Updates',
      description: 'Real-time data refresh ensures current intelligence',
      icon: <RefreshCw className="h-5 w-5" />
    },
    {
      title: 'Confidence Scoring',
      description: 'Every recommendation includes confidence level indicators',
      icon: <BarChart3 className="h-5 w-5" />
    }
  ]

  const securityFeatures = [
    {
      title: 'Bank-Grade Encryption',
      description: 'AES-256 encryption for all data transmission and storage',
      icon: <Lock className="h-5 w-5" />
    },
    {
      title: 'SOC 2 Compliant',
      description: 'Audited security controls and processes',
      icon: <Shield className="h-5 w-5" />
    },
    {
      title: 'Data Isolation',
      description: 'Your data is never shared or used to train models',
      icon: <Database className="h-5 w-5" />
    },
    {
      title: 'NDA Protection',
      description: 'All analyses covered by comprehensive NDAs',
      icon: <FileText className="h-5 w-5" />
    }
  ]

  const faqs = [
    {
      question: "How is this different from ChatGPT or other AI tools?",
      answer: "StratIQX is purpose-built for strategic business analysis. Unlike general AI tools, we access premium business databases, apply proven consulting frameworks, use industry-specific models, and have expert validation. Think of it as having McKinsey-level analysis powered by AI, not just an AI chatbot."
    },
    {
      question: "What types of strategic questions can you analyze?",
      answer: "We handle all major strategic decisions: market entry/expansion, competitive strategy, M&A evaluation, pricing optimization, product portfolio decisions, growth strategy, risk assessment, digital transformation, and operational efficiency. If it's a strategic business decision, we can analyze it."
    },
    {
      question: "How do you ensure the quality of your analysis?",
      answer: "Quality is ensured through multiple layers: AI analysis is based on verified data sources, proprietary algorithms apply proven frameworks, senior analysts review all outputs, confidence scores indicate reliability, and continuous feedback improves our models. Plus, we offer a satisfaction guarantee."
    },
    {
      question: "Can you handle industry-specific analysis?",
      answer: "Absolutely. Our AI models are trained on industry-specific data and frameworks. We have specialized models for technology, healthcare, manufacturing, retail, financial services, and more. Each analysis applies relevant industry benchmarks, regulations, and best practices."
    },
    {
      question: "What if I need to discuss the findings?",
      answer: "All plans include detailed explanations within the reports and access to our knowledge base. Higher-tier plans include priority support through our help system and comprehensive documentation to help you interpret findings and plan implementation."
    },
    {
      question: "How quickly can I get started?",
      answer: "Immediately! Sign up takes 5 minutes, submit your first strategic question right away, and receive your analysis within 4 hours. No lengthy onboarding or setup required."
    }
  ]

  const sampleDeliverables = [
    {
      title: 'Executive Summary',
      description: '2-page overview with key findings and recommendations',
      icon: <FileText className="h-5 w-5" />
    },
    {
      title: 'Detailed Report',
      description: '30-50 page comprehensive analysis with data and models',
      icon: <BookOpen className="h-5 w-5" />
    },
    {
      title: 'Visual Intelligence Reports',
      description: 'Comprehensive charts and infographics for easy digestion',
      icon: <BarChart3 className="h-5 w-5" />
    },
    {
      title: 'Implementation Roadmap',
      description: 'Step-by-step action plan with timelines and milestones',
      icon: <Target className="h-5 w-5" />
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6 inline-flex items-center bg-blue-600/20 px-4 py-2 rounded-full border border-blue-500/30">
            <Brain className="h-5 w-5 text-cyan-400 mr-2" />
            <span className="text-cyan-400 font-semibold">AI + Human Expertise</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white to-cyan-300 bg-clip-text text-transparent">
            How StratIQX Delivers Strategic Intelligence
          </h1>
          
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8">
            From question to actionable insights in 4 hours. See exactly how our AI-powered platform 
            transforms your strategic challenges into data-driven recommendations.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => setShowVideo(true)}
              size="lg"
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
            >
              <Play className="h-5 w-5 mr-2" />
              Watch 2-Min Demo
            </Button>
            <Button
              onClick={handleGetStarted}
              size="lg"
              variant="outline"
              className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
            >
              Start Free Analysis
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Your Journey from Question to Strategic Clarity
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our 5-step process delivers comprehensive strategic intelligence faster than 
              a typical consultant's first meeting
            </p>
          </div>

          {/* Timeline Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between max-w-5xl mx-auto">
              {processSteps.map((step, index) => (
                <div key={index} className="flex-1 relative">
                  <div className="flex items-center">
                    <button
                      onClick={() => setActiveStep(index)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                        activeStep === index 
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white scale-110' 
                          : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                      }`}
                    >
                      {step.number}
                    </button>
                    {index < processSteps.length - 1 && (
                      <div className={`flex-1 h-0.5 ${
                        activeStep > index ? 'bg-cyan-500' : 'bg-slate-700'
                      }`} />
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 absolute left-0 w-24 text-center -ml-6">
                    {step.duration}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Active Step Details */}
          <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-white/20 w-14 h-14 rounded-full flex items-center justify-center mr-4">
                    {processSteps[activeStep].icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      Step {processSteps[activeStep].number}: {processSteps[activeStep].title}
                    </h3>
                    <p className="text-cyan-100">{processSteps[activeStep].description}</p>
                  </div>
                </div>
                <div className="bg-white/10 px-4 py-2 rounded-full">
                  <Clock className="h-5 w-5 text-white inline mr-2" />
                  <span className="text-white font-semibold">{processSteps[activeStep].duration}</span>
                </div>
              </div>
            </div>
            
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">What Happens:</h4>
                  <ul className="space-y-3">
                    {processSteps[activeStep].details.map((detail, index) => (
                      <li key={index} className="flex items-start">
                        <ChevronRight className="h-5 w-5 text-cyan-400 mr-2 mt-0.5" />
                        <span className="text-gray-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {processSteps[activeStep].deliverables && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">What You Get:</h4>
                    <ul className="space-y-3">
                      {processSteps[activeStep].deliverables.map((deliverable, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                          <span className="text-gray-300">{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                  variant="outline"
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
                  disabled={activeStep === 0}
                >
                  Previous Step
                </Button>
                <Button
                  onClick={() => setActiveStep(Math.min(processSteps.length - 1, activeStep + 1))}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
                  disabled={activeStep === processSteps.length - 1}
                >
                  Next Step
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Methodology Deep Dive */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Our Four-Pillar Intelligence Methodology
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Combining the best of AI technology with human expertise to deliver 
              unparalleled strategic insights
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodologyPillars.map((pillar, index) => (
              <Card 
                key={index}
                className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm hover:transform hover:scale-105 transition-all cursor-pointer"
                onClick={() => setExpandedMethod(expandedMethod === index ? null : index)}
              >
                <div className={`bg-gradient-to-r ${pillar.color} px-6 py-4`}>
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mb-3">
                    {pillar.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white">{pillar.title}</h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-300 mb-4">{pillar.description}</p>
                  
                  {expandedMethod === index ? (
                    <div>
                      <ul className="space-y-2">
                        {pillar.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
                            <span className="text-sm text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-4 text-center">
                        <span className="text-xs text-cyan-400">Click to collapse</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <span className="text-sm text-cyan-400 flex items-center justify-center">
                        Learn more
                        <ChevronDown className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality & Security */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Quality Assurance */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                <Award className="h-6 w-6 text-yellow-400 mr-3" />
                Quality Assurance
              </h3>
              <div className="space-y-4">
                {qualityAssurances.map((qa, index) => (
                  <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start">
                        <div className="bg-green-600/20 w-10 h-10 rounded-full flex items-center justify-center mr-4">
                          {qa.icon}
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{qa.title}</h4>
                          <p className="text-gray-400 text-sm">{qa.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Security Features */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                <Shield className="h-6 w-6 text-cyan-400 mr-3" />
                Enterprise Security
              </h3>
              <div className="space-y-4">
                {securityFeatures.map((security, index) => (
                  <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start">
                        <div className="bg-blue-600/20 w-10 h-10 rounded-full flex items-center justify-center mr-4">
                          {security.icon}
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{security.title}</h4>
                          <p className="text-gray-400 text-sm">{security.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Deliverables */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              What You'll Receive
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Every strategic analysis includes comprehensive deliverables designed for immediate action
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sampleDeliverables.map((deliverable, index) => (
              <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-r from-cyan-600 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    {deliverable.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{deliverable.title}</h4>
                  <p className="text-gray-400 text-sm">{deliverable.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  {expandedFAQ === index ? (
                    <ChevronUp className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <CardContent className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">500+</div>
              <p className="text-gray-300">Companies Served</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">10,000+</div>
              <p className="text-gray-300">Analyses Delivered</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">98%</div>
              <p className="text-gray-300">Client Satisfaction</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">4hrs</div>
              <p className="text-gray-300">Average Delivery</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <Card className="bg-gradient-to-r from-cyan-600 to-blue-600 border-cyan-500/30 overflow-hidden">
            <CardContent className="p-12 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
              
              <Sparkles className="h-12 w-12 text-white mx-auto mb-6" />
              
              <h2 className="text-4xl font-bold text-white mb-4">
                See StratIQX in Action
              </h2>
              <p className="text-xl text-cyan-100 mb-8">
                Experience how we transform strategic questions into actionable intelligence in 4 hours
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-xl"
                >
                  Start Your First Analysis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => setShowVideo(true)}
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Watch Demo Video
                </Button>
              </div>
              
              <p className="text-cyan-100 mt-8">
                No credit card required • 30-day money-back guarantee
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <MainFooter />
    </div>
  )
}